import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const PROFILES = [
  { id: 1, name: "Belleza", label: "P1 Belleza" },
  { id: 2, name: "Comercial", label: "P2 Comercial" },
  { id: 3, name: "Salud", label: "P3 Salud" },
  { id: 4, name: "Educación", label: "P4 Educación" },
  { id: 5, name: "Técnico", label: "P5 Técnico" }
];

const defaultHours = DAYS.map((_, i) => ({
  dayOfWeek: i + 1,
  isOpen: i < 5,
  openTime: "09:00",
  closeTime: "18:00",
  slotDuration: 60,
  breakStart: null,
  breakEnd: null
}));

function parseStepFromSearch(search) {
  const params = new URLSearchParams(search);
  const s = params.get("step");
  if (s == null || s === "") return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 0 || n > 6) return null;
  return n;
}

export function useOnboardingWizard() {
  const location = useLocation();
  const initialStep = parseStepFromSearch(location.search);
  const [step, setStep] = useState(initialStep ?? 0);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState(1);
  const [profileName, setProfileName] = useState("Belleza");
  const [businessName, setBusinessName] = useState("");
  const [timezone, setTimezone] = useState("America/Lima");
  const [currency, setCurrency] = useState("PEN");
  const [businessHours, setBusinessHours] = useState(defaultHours);
  const [professionals, setProfessionals] = useState([{ name: "", roleLabel: "", specialty: "" }]);
  const [services, setServices] = useState([{ serviceName: "", description: "", durationMin: 60, price: 0, requiresProfessional: true }]);
  const [activating, setActivating] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/onboarding/config");
      if (data.business) {
        setProfileId(data.business.profileId);
        setProfileName(data.business.profileName || PROFILES.find((p) => p.id === data.business.profileId)?.name || "Belleza");
        setBusinessName(data.business.businessName || "");
        setTimezone(data.business.timezone || "America/Lima");
        setCurrency(data.business.currency || "PEN");
      }
      if (data.businessHours?.length) setBusinessHours(data.businessHours);
      if (data.professionals?.length) setProfessionals(data.professionals.map((p) => ({ name: p.name, roleLabel: p.roleLabel || "", specialty: p.specialty || "" })));
      if (data.services?.length) setServices(data.services.map((s) => ({ serviceName: s.serviceName, description: s.description || "", durationMin: s.durationMin || 60, price: s.price || 0, requiresProfessional: s.requiresProfessional !== false })));
    } catch (e) {
      toastError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    if (initialStep != null) setStep(initialStep);
  }, [initialStep]);

  const nextStep = () => setStep((s) => Math.min(s + 1, 6));
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));
  const goToStep = (s) => setStep(s);

  const updateHours = (dayIndex, field, value) => {
    setBusinessHours((prev) => prev.map((h, i) => (i === dayIndex ? { ...h, [field]: value } : h)));
  };

  const addProfessional = () => setProfessionals((p) => [...p, { name: "", roleLabel: "", specialty: "" }]);
  const removeProfessional = (idx) => setProfessionals((p) => p.filter((_, i) => i !== idx));
  const updateProfessional = (idx, field, value) => {
    setProfessionals((p) => p.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const addService = () => setServices((s) => [...s, { serviceName: "", description: "", durationMin: 60, price: 0, requiresProfessional: true }]);
  const removeService = (idx) => setServices((s) => s.filter((_, i) => i !== idx));
  const updateService = (idx, field, value) => {
    setServices((s) => s.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const saveComplete = async () => {
    try {
      await api.post("/onboarding/complete", {
        profileId,
        profileName,
        businessName,
        timezone,
        currency,
        businessHours,
        professionals: professionals.filter((p) => p.name?.trim()),
        services: services.filter((s) => s.serviceName?.trim())
      });
      return true;
    } catch (e) {
      toastError(e);
      return false;
    }
  };

  const activate = async () => {
    try {
      setActivating(true);
      await api.post("/onboarding/activate", { profileId });
      return true;
    } catch (e) {
      toastError(e);
      return false;
    } finally {
      setActivating(false);
    }
  };

  return {
    step, setStep, nextStep, prevStep, goToStep, loading, activating,
    profileId, setProfileId, profileName, setProfileName, PROFILES,
    businessName, setBusinessName, timezone, setTimezone, currency, setCurrency,
    businessHours, updateHours, DAYS,
    professionals, addProfessional, removeProfessional, updateProfessional,
    services, addService, removeService, updateService,
    saveComplete, activate, fetchConfig
  };
}
